import { type RefObject, type UIEvent, useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_EPSILON = 1;

const getScrollableAncestors = (element: HTMLElement): HTMLElement[] => {
  const ancestors: HTMLElement[] = [];
  let current: HTMLElement | null = element.parentElement;

  while (current != null) {
    const style = getComputedStyle(current);
    const overflow = style.overflow;
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const yScrollable =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflow === 'auto' ||
        overflow === 'scroll') &&
      current.scrollHeight > current.clientHeight + SCROLL_EPSILON;
    const xScrollable =
      (overflowX === 'auto' ||
        overflowX === 'scroll' ||
        overflow === 'auto' ||
        overflow === 'scroll') &&
      current.scrollWidth > current.clientWidth + SCROLL_EPSILON;

    if (yScrollable || xScrollable) {
      ancestors.push(current);
    }

    current = current.parentElement;
  }

  return ancestors;
};

type VirtualScrollbarState = {
  showVirtualScrollbar: boolean;
  virtualScrollbarLeft: number;
  virtualScrollbarWidth: number;
  virtualScrollbarContentWidth: number;
};

const useDataTableVirtualScrollbar = (tableScrollWrapperRef: RefObject<HTMLDivElement | null>) => {
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const virtualScrollbarViewportRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<VirtualScrollbarState>({
    showVirtualScrollbar: false,
    virtualScrollbarLeft: 0,
    virtualScrollbarWidth: 0,
    virtualScrollbarContentWidth: 0,
  });

  const syncVirtualScrollbarPosition = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const virtualScrollbarViewport = virtualScrollbarViewportRef.current;

    if (!scrollContainer || !virtualScrollbarViewport) {
      return;
    }

    if (
      Math.abs(scrollContainer.scrollLeft - virtualScrollbarViewport.scrollLeft) <= SCROLL_EPSILON
    ) {
      return;
    }

    virtualScrollbarViewport.scrollLeft = scrollContainer.scrollLeft;
  }, []);

  useEffect(() => {
    const wrapper = tableScrollWrapperRef.current;

    if (!wrapper) {
      return;
    }

    const scrollContainer = wrapper.querySelector<HTMLElement>('[data-slot="table-container"]');

    if (!scrollContainer) {
      return;
    }

    scrollContainerRef.current = scrollContainer;

    const updateVirtualScrollbar = () => {
      const { innerHeight, innerWidth } = window;
      const wrapperRect = wrapper.getBoundingClientRect();
      const hasHorizontalOverflow =
        scrollContainer.scrollWidth > scrollContainer.clientWidth + SCROLL_EPSILON;
      const isVerticallyVisible = wrapperRect.bottom > 0 && wrapperRect.top < innerHeight;
      const isBottomOutOfView = wrapperRect.bottom > innerHeight + SCROLL_EPSILON;
      const left = Math.max(wrapperRect.left, 0);
      const right = Math.min(wrapperRect.right, innerWidth);
      const width = Math.max(right - left, 0);
      const showVirtualScrollbar =
        hasHorizontalOverflow && isVerticallyVisible && isBottomOutOfView && width > 0;

      setState((current) => {
        if (
          current.showVirtualScrollbar === showVirtualScrollbar &&
          current.virtualScrollbarLeft === left &&
          current.virtualScrollbarWidth === width &&
          current.virtualScrollbarContentWidth === scrollContainer.scrollWidth
        ) {
          return current;
        }

        return {
          showVirtualScrollbar,
          virtualScrollbarLeft: left,
          virtualScrollbarWidth: width,
          virtualScrollbarContentWidth: scrollContainer.scrollWidth,
        };
      });
    };

    const handleRealScroll = () => {
      syncVirtualScrollbarPosition();
    };

    updateVirtualScrollbar();
    syncVirtualScrollbarPosition();

    scrollContainer.addEventListener('scroll', handleRealScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      updateVirtualScrollbar();
      syncVirtualScrollbarPosition();
    });

    resizeObserver.observe(wrapper);
    resizeObserver.observe(scrollContainer);

    const tableElement = scrollContainer.querySelector<HTMLTableElement>('table');

    if (tableElement) {
      resizeObserver.observe(tableElement);
    }

    // Main app content scrolls inside `overflow-auto` panes; those events do not reach `window`.
    const scrollableAncestors = getScrollableAncestors(wrapper);

    const onAncestorOrWindowScroll = () => {
      updateVirtualScrollbar();
    };

    scrollableAncestors.forEach((ancestor) => {
      ancestor.addEventListener('scroll', onAncestorOrWindowScroll, { passive: true });
    });
    window.addEventListener('scroll', onAncestorOrWindowScroll, { passive: true });
    window.addEventListener('resize', updateVirtualScrollbar);

    const visualViewport = window.visualViewport;
    if (visualViewport != null) {
      visualViewport.addEventListener('scroll', onAncestorOrWindowScroll);
      visualViewport.addEventListener('resize', updateVirtualScrollbar);
    }

    return () => {
      scrollContainer.removeEventListener('scroll', handleRealScroll);
      resizeObserver.disconnect();
      scrollableAncestors.forEach((ancestor) => {
        ancestor.removeEventListener('scroll', onAncestorOrWindowScroll);
      });
      window.removeEventListener('scroll', onAncestorOrWindowScroll);
      window.removeEventListener('resize', updateVirtualScrollbar);
      if (visualViewport != null) {
        visualViewport.removeEventListener('scroll', onAncestorOrWindowScroll);
        visualViewport.removeEventListener('resize', updateVirtualScrollbar);
      }
    };
  }, [syncVirtualScrollbarPosition, tableScrollWrapperRef]);

  useEffect(() => {
    syncVirtualScrollbarPosition();
  }, [
    state.showVirtualScrollbar,
    state.virtualScrollbarContentWidth,
    syncVirtualScrollbarPosition,
  ]);

  const onVirtualScrollbarScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    if (Math.abs(scrollContainer.scrollLeft - event.currentTarget.scrollLeft) <= SCROLL_EPSILON) {
      return;
    }

    scrollContainer.scrollLeft = event.currentTarget.scrollLeft;
  }, []);

  return {
    virtualScrollbarViewportRef,
    onVirtualScrollbarScroll,
    ...state,
  };
};

export default useDataTableVirtualScrollbar;
